// @flow
// React
import React, {useEffect, useRef, useState} from 'react';

//Third party
import {observer} from 'mobx-react';
import 'emoji-mart/css/emoji-mart.css';
import {Picker, Emoji} from 'emoji-mart';

// WS
import {Events, Roles} from '@wellstone-solutions/common';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  IconNames,
  TextField,
} from '@wellstone-solutions/web';
import {Actions, Types} from '../../constants/Messages';
import {useStores} from 'hooks/useStores';
import {Palette} from '../../palette';
import {useFeatureFlag, LD_FLAGS} from 'modules/feature-flags';

const ChatModuleInputs = (props: {
  channel: {id: string, name: string},
  chatMember: {id: string, name: string, user: {id: string}},
}) => {
  const {meStore, pubnubStore, eventStore, channelStore} = useStores();
  const {channel, chatMember} = props;
  const [nextMessage, setNextMessage] = useState('');
  const [typingStatus, setTypingStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const typingTimeout = useRef();

  const staffNotificationsEnabled = useFeatureFlag(
    LD_FLAGS.STAFF_NOTIFICATIONS,
  );

  // componentDidMount
  useEffect(() => {
    return () => {
      setTyping(false);
    };
  }, []);

  const setTyping = (isTyping, delayedReset) => {
    clearTimeout(typingTimeout.current);
    if (delayedReset) {
      typingTimeout.current = setTimeout(() => {
        setTyping(false);
      }, 5000);
    }
    if (channel && typingStatus !== isTyping) {
      pubnubStore.setState({
        channels: [channel?.name],
        uuid: meStore.me.id,
        state: {meTyping: isTyping},
      });
      setTypingStatus(isTyping);
    }
  };

  const publishMessage = async (message = '') => {
    if (isLoading || message.length === 0) {
      return false;
    }
    setIsLoading(true);

    const msg = {
      channel: channel?.name,
      data: {
        type: Types.MESSAGE,
        action: Actions.CREATED,
        content: message,
        name: meStore.me.name,
        author: meStore.me.id,
      },
    };

    const eventData = {
      recipient_user_id: chatMember.user.id,
      sender_user_id: meStore.me.id,
      sent_to_staff: Boolean(chatMember && chatMember.role !== Roles.PATIENT),
      sent_by_staff: meStore.isStaffMember,
    };

    if (eventData.sent_to_staff === true && staffNotificationsEnabled) {
      channelStore.scheduleTask(channel.id, chatMember.user.id);
    }

    await pubnubStore.publish(channel?.name, msg);
    eventStore.addEvent(Events.USER_CREATED_MESSAGE, eventData);
    setNextMessage('');
    setTyping(false);
    setIsLoading(false);
  };

  const addEmoji = (emoji) => {
    setNextMessage(nextMessage + emoji.native);
    setIsPickerOpen(false);
  };

  const onInputChange = (newVal) => {
    setTyping(true, true);
    setNextMessage(newVal.target.value);
  };

  const inputBlur = (content) => {
    setTyping(false);
  };

  return (
    <div>
      <div style={styles.inputRow}>
        <TextField
          formField="text"
          placeholder="message here"
          value={nextMessage}
          multiline
          minRows={1}
          maxRows={6}
          fullWidth
          sx={styles.inputField}
          onChange={onInputChange}
          onEnter={(e) => publishMessage(e.target.value)}
          onBlur={() => inputBlur()}
        />
        <IconButton
          onClick={() => publishMessage(nextMessage)}
          icon={IconNames.ArrowRightCircle}
          iconProps={{
            color: Palette.PRIMARY,
            style: nextMessage.length ? styles.enabled : styles.disabled,
          }}
        />
      </div>
      <Box>
        <Button
          data-testid="emoji-button"
          variant="contained"
          onClick={() => setIsPickerOpen(true)}
          sx={styles.emojiButton}>
          <Emoji emoji=":grinning:" set="apple" size={24} />
        </Button>
      </Box>
      <Dialog
        open={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        PaperProps={{sx: styles.pickerDialog}}>
        <Picker
          onSelect={addEmoji}
          darkMode={false}
          title="Pick an emoji…"
          emoji="point_up"
          perLine={12}
        />
      </Dialog>
    </div>
  );
};

const styles = {
  inputRow: {
    display: 'flex',
    marginBottom: 10,
    alignItems: 'center',
  },
  inputField: {
    marginRight: '10px',
    backgroundColor: 'white',
  },

  emojiButton: {
    marginLeft: '10px',
    minWidth: 0,
    borderRadius: '24px',
    padding: 1,
  },
  pickerDialog: {
    padding: 3,
  },
  enabled: {
    cursor: 'pointer',
  },
  disabled: {
    opacity: 0.5,
  },
};

export default observer(ChatModuleInputs);
